/**
 * Dialog de configuração para node de remoção de tags
 * Suporta tags de contato e negócio (CRM)
 * Permite remover tag específica ou todas as tags
 * 
 * RELEASE: 1.0.0 - 2025-01-15 - Novo dialog
 */

import { useState, useEffect, useRef } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { FlowNode, RemoveTagConfig } from '@/types/flows';
import { z } from 'zod';
import { toast } from 'sonner';
import { useAuth } from '@/hooks/useAuth';
import { supabase } from '@/integrations/supabase/client';
import { Loader2, AlertTriangle } from 'lucide-react';
import { NodeIdField, NodeIdFieldRef } from '../NodeIdField';
import { Separator } from '@/components/ui/separator';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { VariablePickerCombobox } from '../VariablePickerCombobox';

/**
 * Schema de validação para remoção de tag
 */
const removeTagSchema = z.object({
  scope: z.enum(['contact', 'deal']),
  mode: z.enum(['specific', 'all']),
  tag_id: z.string().uuid().optional(),
  tag_name: z.string().optional(),
  tag_color: z.string().optional(),
  deal_selection: z.enum(['most_recent', 'from_variable']).optional(),
  deal_status_filter: z.enum(['open', 'won', 'lost', 'all']).optional(),
  deal_variable_id: z.string().optional(),
  deal_variable_name: z.string().optional(),
}).refine((data) => {
  // Se modo específico, tag_id é obrigatório
  if (data.mode === 'specific') {
    return !!data.tag_id;
  }
  return true;
}, {
  message: 'Selecione uma tag para remover',
  path: ['tag_id'],
}).refine((data) => {
  // Se modo from_variable, precisa ter variável selecionada
  if (data.scope === 'deal' && data.deal_selection === 'from_variable') {
    return !!data.deal_variable_id || !!data.deal_variable_name;
  }
  return true;
}, {
  message: 'Selecione uma variável com o ID do negócio',
  path: ['deal_variable_id'],
});

interface ConfigureRemoveTagDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  node: FlowNode | null;
  onSave: (nodeId: string, config: any) => void;
}

/**
 * Dialog de configuração para remover tags
 */
export function ConfigureRemoveTagDialog({ 
  open, 
  onOpenChange, 
  node, 
  onSave 
}: ConfigureRemoveTagDialogProps) {
  const { currentTenant } = useAuth();
  const [loadingTags, setLoadingTags] = useState(false);
  /** Ref para capturar apelido do node */
  const nicknameRef = useRef<NodeIdFieldRef>(null);
  
  // Estados do formulário
  const [scope, setScope] = useState<'contact' | 'deal'>('contact');
  const [mode, setMode] = useState<'specific' | 'all'>('specific');
  const [tagId, setTagId] = useState('');
  const [tags, setTags] = useState<Array<{ id: string; name: string; color: string }>>([]);
  
  // Estados para identificação de deal
  const [dealSelection, setDealSelection] = useState<'most_recent' | 'from_variable'>('most_recent');
  const [dealStatusFilter, setDealStatusFilter] = useState<'open' | 'won' | 'lost' | 'all'>('open');
  const [dealVariableId, setDealVariableId] = useState('');
  const [dealVariableName, setDealVariableName] = useState('');
  
  // Flag para evitar limpeza de tagId durante inicialização
  const isInitializing = useRef(true);

  /**
   * Carrega tags baseado no escopo selecionado
   */
  const loadTags = async () => {
    if (!currentTenant?.id) return;
    
    setLoadingTags(true);
    try {
      if (scope === 'contact') {
        const { data, error } = await supabase
          .from('contact_tags')
          .select('id, name, color')
          .eq('tenant_id', currentTenant.id)
          .order('name');

        if (error) {
          toast.error('Erro ao carregar tags de contato');
          return;
        }
        setTags(data || []);
      } else {
        const { data, error } = await supabase
          .from('crm_deal_tags')
          .select('id, name, color')
          .eq('tenant_id', currentTenant.id)
          .order('name');

        if (error) {
          toast.error('Erro ao carregar tags de negócio');
          return;
        }
        setTags(data || []);
      }
    } finally {
      setLoadingTags(false);
    }
  };

  useEffect(() => {
    if (open && currentTenant?.id) {
      loadTags();
    }
  }, [open, currentTenant?.id, scope]);

  // Inicializa valores do node
  useEffect(() => {
    if (node && node.data.config) {
      const config = node.data.config as RemoveTagConfig;
      // Marca que está inicializando para não limpar tagId no useEffect do scope
      isInitializing.current = true;
      setScope(config.scope || 'contact');
      setMode(config.mode || 'specific');
      setTagId(config.tag_id || '');
      setDealSelection(config.deal_selection || 'most_recent');
      setDealStatusFilter(config.deal_status_filter || 'open');
      setDealVariableId(config.deal_variable_id || '');
      setDealVariableName(config.deal_variable_name || '');
      // Libera flag após pequeno delay para permitir que useEffect do scope execute primeiro
      setTimeout(() => { isInitializing.current = false; }, 0);
    } else {
      setScope('contact');
      setMode('specific');
      setTagId('');
      setDealSelection('most_recent');
      setDealStatusFilter('open');
      setDealVariableId('');
      setDealVariableName('');
      isInitializing.current = false;
    }
  }, [node]);

  // Limpa seleção de tag ao mudar escopo (apenas se não for inicialização)
  useEffect(() => {
    // Durante inicialização, não limpar os valores carregados da config
    if (isInitializing.current) return;
    
    setTagId('');
  }, [scope]);

  const handleSave = () => {
    if (!node) return;

    try {
      const selectedTag = tags.find(t => t.id === tagId);
      
      const config: RemoveTagConfig = {
        scope,
        mode,
        tag_id: mode === 'specific' ? tagId : undefined,
        tag_name: mode === 'specific' ? selectedTag?.name : undefined,
        tag_color: mode === 'specific' ? selectedTag?.color : undefined,
        deal_selection: scope === 'deal' ? dealSelection : undefined,
        deal_status_filter: scope === 'deal' && dealSelection === 'most_recent' ? dealStatusFilter : undefined,
        deal_variable_id: scope === 'deal' && dealSelection === 'from_variable' ? dealVariableId : undefined,
        deal_variable_name: scope === 'deal' && dealSelection === 'from_variable' ? dealVariableName : undefined,
      };

      removeTagSchema.parse(config);

      onSave(node.id, { ...config, nickname: nicknameRef.current?.getNickname() });
      toast.success('Configuração salva com sucesso');
      onOpenChange(false);
    } catch (error) {
      if (error instanceof z.ZodError) {
        toast.error(error.errors[0].message);
      } else {
        toast.error('Erro ao salvar configuração');
      }
    }
  };

  if (!node) return null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Configurar Remoção de Tag</DialogTitle>
        </DialogHeader>

        <div className="space-y-6 py-4">
          <NodeIdField ref={nicknameRef} nodeId={node.id} defaultNickname={node.data.config?.nickname} />
          
          {/* Tipo de Tag */}
          <div className="space-y-3">
            <Label className="text-sm font-medium">Tipo de Tag *</Label>
            <RadioGroup value={scope} onValueChange={(v) => setScope(v as 'contact' | 'deal')} className="flex flex-col gap-2">
              <div className="flex items-center space-x-2 p-3 border rounded-lg hover:bg-accent/50 cursor-pointer">
                <RadioGroupItem value="contact" id="scope-contact" />
                <Label htmlFor="scope-contact" className="flex-1 cursor-pointer">
                  <div className="font-medium">Tag de Contato</div>
                  <div className="text-xs text-muted-foreground">Tags associadas ao contato</div>
                </Label>
              </div>
              <div className="flex items-center space-x-2 p-3 border rounded-lg hover:bg-accent/50 cursor-pointer">
                <RadioGroupItem value="deal" id="scope-deal" />
                <Label htmlFor="scope-deal" className="flex-1 cursor-pointer">
                  <div className="font-medium">Tag de Negócio (CRM)</div>
                  <div className="text-xs text-muted-foreground">Tags associadas a negócios</div>
                </Label>
              </div>
            </RadioGroup>
          </div>

          <Separator />

          {/* Modo de Remoção */}
          <div className="space-y-3">
            <Label className="text-sm font-medium">Modo de Remoção *</Label>
            <RadioGroup value={mode} onValueChange={(v) => setMode(v as 'specific' | 'all')} className="flex flex-col gap-2">
              <div className="flex items-center space-x-2 p-3 border rounded-lg hover:bg-accent/50 cursor-pointer">
                <RadioGroupItem value="specific" id="mode-specific" />
                <Label htmlFor="mode-specific" className="flex-1 cursor-pointer">
                  <div className="font-medium">Remover tag específica</div>
                </Label>
              </div>
              <div className="flex items-center space-x-2 p-3 border rounded-lg hover:bg-accent/50 cursor-pointer">
                <RadioGroupItem value="all" id="mode-all" />
                <Label htmlFor="mode-all" className="flex-1 cursor-pointer">
                  <div className="font-medium">Remover todas as tags</div>
                </Label>
              </div>
            </RadioGroup>
          </div>

          {mode === 'specific' && (
            <div className="space-y-2">
              <Label>Tag a Remover *</Label>
              <Select value={tagId} onValueChange={setTagId} disabled={loadingTags}>
                <SelectTrigger>
                  <SelectValue placeholder={loadingTags ? 'Carregando...' : 'Selecione uma tag'} />
                </SelectTrigger>
                <SelectContent>
                  {loadingTags ? (
                    <div className="flex items-center justify-center p-4">
                      <Loader2 className="h-4 w-4 animate-spin mr-2" />Carregando...
                    </div>
                  ) : tags.length === 0 ? (
                    <div className="p-3 text-sm text-muted-foreground text-center">Nenhuma tag cadastrada.</div>
                  ) : (
                    tags.map((tag) => (
                      <SelectItem key={tag.id} value={tag.id}>
                        <div className="flex items-center gap-2">
                          <div className="w-3 h-3 rounded-full" style={{ backgroundColor: tag.color || '#6366f1' }} />
                          {tag.name}
                        </div>
                      </SelectItem>
                    ))
                  )}
                </SelectContent>
              </Select>
            </div>
          )}

          {mode === 'all' && (
            <Alert variant="destructive">
              <AlertTriangle className="h-4 w-4" />
              <AlertDescription>
                Todas as tags serão removidas do {scope === 'contact' ? 'contato' : 'negócio'}.
              </AlertDescription>
            </Alert>
          )}

          {scope === 'deal' && (
            <>
              <Separator />
              <div className="space-y-4">
                <Label className="text-sm font-medium">Identificar Negócio</Label>
                
                {/* RadioGroup para modo de seleção */}
                <RadioGroup
                  value={dealSelection}
                  onValueChange={(v) => setDealSelection(v as 'most_recent' | 'from_variable')}
                  className="space-y-2"
                >
                  <div className="flex items-start space-x-3 p-3 rounded-lg border hover:bg-accent/50 cursor-pointer">
                    <RadioGroupItem value="most_recent" id="remove-deal-most-recent" className="mt-0.5" />
                    <div className="space-y-1 flex-1">
                      <Label htmlFor="remove-deal-most-recent" className="font-medium cursor-pointer">
                        Negócio mais recente do contato
                      </Label>
                      <p className="text-xs text-muted-foreground">
                        Busca automaticamente o negócio mais recente associado ao contato
                      </p>
                    </div>
                  </div>
                  
                  <div className="flex items-start space-x-3 p-3 rounded-lg border hover:bg-accent/50 cursor-pointer">
                    <RadioGroupItem value="from_variable" id="remove-deal-from-variable" className="mt-0.5" />
                    <div className="space-y-1 flex-1">
                      <Label htmlFor="remove-deal-from-variable" className="font-medium cursor-pointer">
                        ID do negócio em variável
                      </Label>
                      <p className="text-xs text-muted-foreground">
                        Usa o ID do negócio armazenado em uma variável do fluxo
                      </p>
                    </div>
                  </div>
                </RadioGroup>

                {/* Filtro de status (apenas para most_recent) */}
                {dealSelection === 'most_recent' && (
                  <div className="space-y-2 pl-4 border-l-2 border-primary/30">
                    <Label>Filtro de Status</Label>
                    <Select value={dealStatusFilter} onValueChange={(v) => setDealStatusFilter(v as any)}>
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="open">Aberto</SelectItem>
                        <SelectItem value="won">Ganho</SelectItem>
                        <SelectItem value="lost">Perdido</SelectItem>
                        <SelectItem value="all">Qualquer</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                )}

                {/* VariablePickerCombobox (apenas para from_variable) */}
                {dealSelection === 'from_variable' && (
                  <div className="space-y-2 pl-4 border-l-2 border-primary/30">
                    <Label>Variável com ID do Negócio *</Label>
                    <VariablePickerCombobox
                      tenantId={currentTenant?.id || ''}
                      value={dealVariableId || dealVariableName}
                      onChange={(variable) => {
                        if (variable) {
                          setDealVariableId(variable.id);
                          // CORREÇÃO: Usar display_name para exportação correta de variáveis
                          setDealVariableName(variable.display_name || variable.name);
                        } else {
                          setDealVariableId('');
                          setDealVariableName('');
                        }
                      }}
                      allowCreate={false}
                      allowClear={false}
                      placeholder="Selecione a variável com o ID do negócio"
                    />
                    <p className="text-xs text-muted-foreground">
                      Use "ID do Negócio" (gatilho CRM) ou variável do node "Criar Negócio"
                    </p>
                  </div>
                )}
              </div>
            </>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancelar</Button>
          <Button onClick={handleSave}>Salvar</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
